import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { MIKOM_API_IMAGE } from '../utils/constants';

const imageCache = new Map();

const isDataUrl = (value) => typeof value === 'string' && value.startsWith('data:');
const isBlobUrl = (value) => typeof value === 'string' && value.startsWith('blob:');
const isHttpUrl = (value) =>
  typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'));

const createBlobUrlFromBase64 = (base64, mimeType) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mimeType });
  return URL.createObjectURL(blob);
};

const normalizeImageSource = (data, mimeTypeHint) => {
  if (!data || typeof data !== 'string') {
    return null;
  }

  if (isHttpUrl(data) || isBlobUrl(data)) {
    return data;
  }

  if (isDataUrl(data)) {
    const match = data.match(/^data:([^;]+);base64,(.*)$/);
    if (match) {
      return createBlobUrlFromBase64(match[2], match[1]);
    }
    return data;
  }

  const mimeType = mimeTypeHint || 'image/jpeg';
  return createBlobUrlFromBase64(data, mimeType);
};

const useGetImages = (picture, firmaId, categoryId, options = {}) => {
  const { observeRef, enabled = true, rootMargin = '200px' } = options;
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const objectUrlRef = useRef(null);
  const [shouldFetch, setShouldFetch] = useState(!observeRef && enabled);

  useEffect(() => {
    if (!enabled) {
      setShouldFetch(false);
      return;
    }

    if (!observeRef) {
      setShouldFetch(true);
      return;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldFetch(true);
      return;
    }

    const node = observeRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldFetch(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [observeRef, enabled, rootMargin]);

  useEffect(() => {
    if (!shouldFetch) {
      return undefined;
    }

    (async () => {
      try {
        const cacheKey = `${picture}:${firmaId}:${categoryId}`;
        const cached = imageCache.get(cacheKey);

        if (cached) {
          const cachedUrl = normalizeImageSource(cached.data, cached.contentType);
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
          }
          if (cachedUrl && cachedUrl.startsWith('blob:')) {
            objectUrlRef.current = cachedUrl;
          }
          setImage(cachedUrl);
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        const response = await axios.get(
          `${MIKOM_API_IMAGE}${picture}/${firmaId}/${categoryId}`
        );
        const normalized = normalizeImageSource(
          response?.data,
          response?.headers?.['content-type']
        );

        imageCache.set(cacheKey, {
          data: response?.data,
          contentType: response?.headers?.['content-type'],
        });

        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = null;
        }

        if (normalized && normalized.startsWith('blob:')) {
          objectUrlRef.current = normalized;
        }

        setImage(normalized);
      } catch (err) {
        console.log(err.response);
        setError(err.response);
      } finally {
        setIsLoading(false);
      }
    })();
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [picture, firmaId, categoryId, shouldFetch]);

  return { image, isLoading, error };
};

export default useGetImages;
